/**
 * Mailchimp Integration for Email Signups
 * This script handles form submissions to Mailchimp API
 */

class MailchimpIntegration {
  constructor() {
    this.config = {
      // These will be set from theme settings
      apiKey: null,
      serverPrefix: null,
      listId: null,
      endpoint: null
    };
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    // Get configuration from theme settings (will be injected by Liquid)
    this.loadConfig();
    
    // Attach event listeners to forms
    this.attachFormListeners();
  }

  loadConfig() {
    // Configuration will be injected by the theme settings
    const configElement = document.getElementById('mailchimp-config');
    if (configElement) {
      try {
        this.config = JSON.parse(configElement.textContent);
      } catch (error) {
        console.warn('Mailchimp config not found or invalid:', error);
      }
    }
  }

  attachFormListeners() {
    // Only attach listeners if Mailchimp is properly configured
    if (!this.config.enabled || !this.config.endpoint || !this.config.listId) {
      console.log('Mailchimp integration not configured - forms will use default Shopify behavior');
      return;
    }

    // Find all newsletter forms and attach listeners
    const forms = document.querySelectorAll('.newsletter-form, .podcast-signup-form');
    
    console.log(`Mailchimp integration: Found ${forms.length} forms to handle`);
    
    forms.forEach(form => {
      // Skip if already has mailchimp listener
      if (form.dataset.mailchimpEnabled) return;
      
      console.log('Attaching Mailchimp listener to form:', form);
      form.dataset.mailchimpEnabled = 'true';
      
      // Remove any existing event listeners to avoid conflicts
      form.removeEventListener('submit', this.handleFormSubmit);
      form.addEventListener('submit', (e) => this.handleFormSubmit(e, form));
    });
    
    // Also handle forms that might be added dynamically
    this.observeForNewForms();
  }

  observeForNewForms() {
    // Watch for dynamically added forms
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            const newForms = node.querySelectorAll ? 
              node.querySelectorAll('.newsletter-form, .podcast-signup-form') : [];
            
            newForms.forEach(form => {
              if (!form.dataset.mailchimpEnabled) {
                console.log('Found new form, attaching Mailchimp listener:', form);
                form.dataset.mailchimpEnabled = 'true';
                form.addEventListener('submit', (e) => this.handleFormSubmit(e, form));
              }
            });
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  async handleFormSubmit(event, form) {
    event.preventDefault();
    
    const emailInput = form.querySelector('input[type="email"]');
    const submitButton = form.querySelector('button[type="submit"]');
    const email = emailInput?.value?.trim();

    if (!email) {
      this.showError(form, 'Please enter a valid email address.');
      return;
    }

    if (!this.isValidEmail(email)) {
      this.showError(form, 'Please enter a valid email address.');
      return;
    }

    // Check if Mailchimp is configured
    if (!this.config.enabled || !this.config.endpoint || !this.config.listId) {
      console.log('Mailchimp custom integration not configured, using Shopify native integration');
      this.fallbackToShopify(form);
      return;
    }

    // Show loading state
    this.setLoadingState(submitButton, true);
    this.clearMessages(form);

    try {
      // Get additional form data
      const formData = this.extractFormData(form);
      
      // Submit to Mailchimp
      await this.submitToMailchimp(email, formData);
      
      // Show success message
      this.showSuccess(form, 'Thank you for subscribing!');
      
      // Reset form
      form.reset();
      
      // Track the signup (if analytics are available)
      this.trackSignup(email, formData.tags);
      
    } catch (error) {
      console.error('Mailchimp submission error:', error);
      
      // Show error message
      if (error.message.includes('already subscribed')) {
        this.showError(form, 'You are already subscribed to our newsletter.');
      } else {
        this.showError(form, 'Something went wrong. Please try again.');
      }
    } finally {
      this.setLoadingState(submitButton, false);
    }
  }

  extractFormData(form) {
    const data = {
      tags: [],
      source: 'website'
    };

    // Extract tags from hidden inputs
    const tagInputs = form.querySelectorAll('input[name*="tags"]');
    tagInputs.forEach(input => {
      if (input.value) {
        // Split comma-separated tags
        const tags = input.value.split(',').map(tag => tag.trim());
        data.tags.push(...tags);
      }
    });

    // Determine source from form class or context
    if (form.classList.contains('podcast-signup-form')) {
      data.source = 'podcast-signup';
      if (!data.tags.includes('podcast-newsletter')) {
        data.tags.push('podcast-newsletter');
      }
    } else if (form.classList.contains('newsletter-form')) {
      data.source = 'newsletter';
      // Check if it's general newsletter or podcast newsletter based on tags
      if (data.tags.includes('general-newsletter')) {
        data.source = 'general-newsletter';
      } else if (data.tags.includes('podcast-newsletter')) {
        data.source = 'podcast-newsletter';
      } else {
        data.tags.push('general-newsletter');
      }
    }

    return data;
  }

  async submitToMailchimp(email, formData) {
    const url = this.config.endpoint;
    
    const payload = {
      email: email,
      tags: formData.tags,
      apiKey: this.config.apiKey,
      serverPrefix: this.config.serverPrefix,
      listId: this.config.listId,
      doubleOptin: this.config.doubleOptin,
      source: formData.source
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      if (response.status === 400 && errorData.error === 'already_subscribed') {
        throw new Error('User already subscribed');
      }
      
      throw new Error(`HTTP ${response.status}: ${errorData.error || 'Unknown error'}`);
    }

    return response.json();
  }

  fallbackToShopify(form) {
    console.log('Falling back to Shopify form submission');
    
    // Remove our event listener and let the form submit normally
    form.removeEventListener('submit', this.handleFormSubmit);
    
    // Remove the mailchimp flag so it doesn't interfere
    delete form.dataset.mailchimpEnabled;
    
    // Let the form submit naturally to Shopify
    form.submit();
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  setLoadingState(button, isLoading) {
    if (!button) return;

    if (isLoading) {
      button.disabled = true;
      button.dataset.originalText = button.textContent;
      button.textContent = 'Submitting...';
    } else {
      button.disabled = false;
      button.textContent = button.dataset.originalText || 'Subscribe';
    }
  }

  clearMessages(form) {
    const messages = form.querySelectorAll('.form-message, .newsletter-form__message');
    messages.forEach(msg => msg.remove());
  }

  showSuccess(form, message) {
    this.clearMessages(form);
    
    const successDiv = document.createElement('div');
    successDiv.className = 'form-message form-message--success newsletter-form__message newsletter-form__message--success';
    successDiv.innerHTML = `
      <span class="svg-wrapper">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M8.33333 15L3.33333 10L4.74999 8.58333L8.33333 12.1667L15.25 5.25L16.6667 6.66667L8.33333 15Z" fill="currentColor"/>
        </svg>
      </span>
      ${message}
    `;
    
    // Insert after the form field wrapper
    const fieldWrapper = form.querySelector('.newsletter-form__field-wrapper, .form-group');
    if (fieldWrapper) {
      fieldWrapper.parentNode.insertBefore(successDiv, fieldWrapper.nextSibling);
    } else {
      form.appendChild(successDiv);
    }
  }

  showError(form, message) {
    this.clearMessages(form);
    
    const errorDiv = document.createElement('div');
    errorDiv.className = 'form-message form-message--error newsletter-form__message';
    errorDiv.innerHTML = `
      <span class="svg-wrapper">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="currentColor"/>
        </svg>
      </span>
      ${message}
    `;
    
    // Insert after the form field wrapper
    const fieldWrapper = form.querySelector('.newsletter-form__field-wrapper, .form-group');
    if (fieldWrapper) {
      fieldWrapper.parentNode.insertBefore(errorDiv, fieldWrapper.nextSibling);
    } else {
      form.appendChild(errorDiv);
    }
  }

  trackSignup(email, tags) {
    // Track with Google Analytics if available
    if (typeof gtag !== 'undefined') {
      gtag('event', 'newsletter_signup', {
        'custom_parameter': tags.join(',')
      });
    }

    // Track with other analytics platforms as needed
    if (typeof fbq !== 'undefined') {
      fbq('track', 'Lead');
    }
  }
}

// Initialize the integration
window.mailchimpIntegration = new MailchimpIntegration();
